import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../../authToken';
import { API_BASE_URL } from '../../config';

const ENDPOINT_HOST = API_BASE_URL;
const ENDPOINT_TEMPLATES = `${ENDPOINT_HOST}/foodintaketemplate`;

const sortByBrandThenNameAsc = (arr) => {
  const a = Array.isArray(arr) ? [...arr] : [];
  a.sort((x, y) => {
    const xb = String(x?.brand ?? '').toLowerCase();
    const yb = String(y?.brand ?? '').toLowerCase();
    if (xb !== yb) return xb.localeCompare(yb);
    const xn = String(x?.name ?? '').toLowerCase();
    const yn = String(y?.name ?? '').toLowerCase();
    return xn.localeCompare(yn);
  });
  return a;
};

/**
 * FoodIntakeTemplatePicker
 * - Lists all templates in a compact one-liner form: "[brand] name" (or just name).
 * - Lets the user select exactly one.
 * - OK returns the selected item via onOk(selectedTemplate)
 * - Cancel calls onCancel()
 */
export default function FoodIntakeTemplatePicker({
  onOk,
  onCancel,
  initialSelectedId = null,
  height = 360,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedId, setSelectedId] = useState(initialSelectedId);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(ENDPOINT_TEMPLATES, {
        headers: {
          ...(authToken ? { 'Authorization': authToken } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch templates (${response.status})`);
      }

      const data = await response.json();
  setItems(sortByBrandThenNameAsc(data));
    } catch (e) {
      console.error('Failed to fetch templates:', e);
      setError(e?.message || 'Failed to fetch templates');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedItems = useMemo(() => sortByBrandThenNameAsc(items), [items]);

  const selectedItem = useMemo(() => {
    if (selectedId === null || selectedId === undefined) return null;
    return sortedItems.find((x) => x?.id === selectedId) || null;
  }, [sortedItems, selectedId]);

  const canOk = !!selectedItem;

  const formatOneLiner = (it) => {
    const br = String(it?.brand ?? '').trim();
    const nm = String(it?.name ?? '').trim();
    const portion = (it?.defaultPortion !== null && it?.defaultPortion !== undefined)
      ? `${it.defaultPortion}${String(it?.unitName ?? '').trim() ? ` ${String(it.unitName).trim()}` : ''}`
      : null;
    const base = br ? `[${br}] ${nm || '(no name)'}` : (nm || '(no name)');
    return portion ? `${base} — ${portion}` : base;
  };

  const buttonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '110px'
  };

  return (
    <div style={{
      padding: '10px 0',
      maxWidth: '520px',
      margin: '0 auto',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div>
        <h3 style={{ margin: '0 0 12px 0', textAlign: 'center' }}>📦 Pick Food Template</h3>

        {loading && <div style={{ textAlign: 'center', color: '#666' }}>Loading...</div>}
        {error && <div style={{ textAlign: 'center', color: 'red' }}>Error: {error}</div>}

        {!loading && !error && sortedItems.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '10px 0' }}>
            No templates.
          </div>
        )}

        {!loading && !error && sortedItems.length > 0 && (
          <div style={{
            maxHeight: `${height}px`,
            overflowY: 'auto',
            border: '1px solid #eee',
            borderRadius: 10,
            padding: 6,
            background: '#fff'
          }}>
            {sortedItems.map((it) => {
              const isSelected = it?.id === selectedId;
              return (
                <div
                  key={it?.id ?? `${it?.brand}-${it?.name}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(it?.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setSelectedId(it?.id);
                  }}
                  style={{
                    padding: '8px 10px',
                    margin: '4px 0',
                    borderRadius: 8,
                    cursor: 'pointer',
                    border: `1px solid ${isSelected ? '#00a2ff' : '#e9ecef'}`,
                    background: isSelected ? '#eaf6ff' : '#f8f9fa',
                    fontSize: 14,
                    fontWeight: isSelected ? 800 : 600,
                    color: '#222',
                    lineHeight: 1.1,
                    userSelect: 'none'
                  }}
                  title={formatOneLiner(it)}
                >
                  {formatOneLiner(it)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 14 }}>
        <button
          onClick={() => typeof onCancel === 'function' && onCancel()}
          style={{
            ...buttonStyle,
            backgroundColor: '#6c757d',
            color: 'white'
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            if (!canOk) return;
            if (typeof onOk === 'function') onOk(selectedItem);
          }}
          disabled={!canOk}
          style={{
            ...buttonStyle,
            backgroundColor: '#ff8a00',
            color: 'white',
            opacity: canOk ? 1 : 0.6,
            cursor: canOk ? 'pointer' : 'not-allowed'
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
}
